import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';

export default function JiraConfig() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');

  // ── Global connection config ─────────────────────────────────────────────
  const [globalConfig, setGlobalConfig] = useState({
    jiraUrl: '', jiraUser: '', apiToken: '',
    defaultProjectKey: '', enabled: true,
  });
  const [globalSaved, setGlobalSaved] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [configMeta, setConfigMeta] = useState(null); // { hasToken, lastTestedOk, lastTestedUser, lastTestedAt, envConfigured }

  // ── Live test (before saving) ────────────────────────────────────────────
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // ── JIRA projects (fetched after connect) ────────────────────────────────
  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // ── Per-project config ───────────────────────────────────────────────────
  const [projectConfigForm, setProjectConfigForm] = useState({ projectKey: '' });
  const [savingProject, setSavingProject] = useState(false);
  const [projectSaved, setProjectSaved] = useState(false);
  const [projectName, setProjectName] = useState('');

  const [error, setError] = useState('');
  const isConnected = configMeta?.lastTestedOk || configMeta?.envConfigured;

  // ── Load global config on mount ──────────────────────────────────────────
  useEffect(() => {
    loadGlobalConfig();
  }, []);

  useEffect(() => {
    if (projectId) loadProjectConfig();
  }, [projectId]);

  useEffect(() => {
    if (isConnected) loadJiraProjects();
  }, [isConnected]);

  async function loadGlobalConfig() {
    try {
      const res = await api.get('/jira/global-config');
      setConfigMeta(res.data);
      setGlobalConfig(prev => ({
        ...prev,
        jiraUrl: res.data.jiraUrl || '',
        jiraUser: res.data.jiraUser || '',
        defaultProjectKey: res.data.defaultProjectKey || '',
        enabled: res.data.enabled ?? true,
      }));
    } catch (err) {
      console.error('Failed to load global JIRA config:', err.message);
    }
  }

  async function loadProjectConfig() {
    try {
      const [cfgRes, projRes] = await Promise.all([
        api.get(`/jira/project-config/${projectId}`),
        api.get(`/projects/${projectId}`),
      ]);
      const cfg = cfgRes.data;
      setProjectConfigForm({ projectKey: cfg.projectKey || '' });
      setProjectName(projRes.data?.name || projRes.data?.data?.name || '');
    } catch (err) {
      console.error('Failed to load project JIRA config:', err.message);
    }
  }

  // ── Test credentials (live, before saving) ───────────────────────────────
  async function testCredentials() {
    const { jiraUrl, jiraUser, apiToken } = globalConfig;
    if (!jiraUrl || !jiraUser || !apiToken) {
      setGlobalError('Fill in JIRA URL, email, and API token before testing.');
      return;
    }
    setTesting(true);
    setTestResult(null);
    setGlobalError('');
    try {
      const res = await api.post('/jira/test-connection', { jiraUrl, jiraUser, apiToken });
      setTestResult(res.data);
      if (res.data.connected) {
        loadJiraProjects();
      }
    } catch (err) {
      setTestResult({ connected: false, error: err.response?.data?.error || err.message });
    } finally {
      setTesting(false);
    }
  }

  // ── Save global config ───────────────────────────────────────────────────
  async function saveGlobalConfig() {
    setSavingGlobal(true);
    setGlobalSaved(false);
    setGlobalError('');
    try {
      await api.put('/jira/global-config', globalConfig);
      setGlobalSaved(true);
      await loadGlobalConfig();
      setTimeout(() => setGlobalSaved(false), 4000);
    } catch (err) {
      setGlobalError(err.response?.data?.error || err.message);
    } finally {
      setSavingGlobal(false);
    }
  }

  // ── Disconnect global config ─────────────────────────────────────────────
  async function disconnectJira() {
    if (!window.confirm('Remove the global JIRA configuration from the database?')) return;
    try {
      await api.delete('/jira/global-config');
      setConfigMeta(null);
      setGlobalConfig({ jiraUrl: '', jiraUser: '', apiToken: '', defaultProjectKey: '', enabled: true });
      setTestResult(null);
      setJiraProjects([]);
    } catch (err) {
      setGlobalError(err.response?.data?.error || err.message);
    }
  }

  async function loadJiraProjects() {
    setLoadingProjects(true);
    try {
      const res = await api.get('/jira/projects');
      setJiraProjects(res.data.data || []);
    } catch {
      // Not yet connected — silent
    } finally {
      setLoadingProjects(false);
    }
  }

  async function saveProjectConfig() {
    setSavingProject(true);
    setProjectSaved(false);
    setError('');
    try {
      await api.put(`/jira/project-config/${projectId}`, projectConfigForm);
      setProjectSaved(true);
      setTimeout(() => setProjectSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingProject(false);
    }
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title d-flex align-items-center gap-2">
            🔗 JIRA Integration
            {isConnected && (
              <span className="badge bg-success" style={{ fontSize: '11px', fontWeight: 600 }}>● Connected</span>
            )}
          </h1>
          <p className="page-subtitle">
            Connect ZipQA to Atlassian JIRA to link requirements, test cases, and file bugs from failed runs.
          </p>
        </div>

      </div>

      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* ── Section 1: Global Connection ── */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>Global JIRA Connection</span>
            {configMeta?.lastTestedOk && (
              <small className="text-success">
                ✅ Verified as <strong>{configMeta.lastTestedUser}</strong>
                {configMeta.lastTestedAt && ` · ${new Date(configMeta.lastTestedAt).toLocaleString()}`}
              </small>
            )}
            {configMeta?.envConfigured && !configMeta?.lastTestedOk && (
              <small style={{ color: 'var(--zq-text-muted)' }}>⚙️ Using .env fallback</small>
            )}
          </div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 20 }}>
              Enter your Atlassian credentials below. They will be saved in the database — no need to edit <code>.env</code> files.
              Generate an API token at{' '}
              <a href="https://id.atlassian.com/manage/api-tokens" target="_blank" rel="noopener noreferrer">
                id.atlassian.com/manage/api-tokens
              </a>.
            </p>

            {globalError && (
              <div className="alert alert-danger alert-dismissible">
                {globalError}
                <button className="btn-close" onClick={() => setGlobalError('')}></button>
              </div>
            )}

            <div className="row g-3 mb-4">
              <div className="col-md-5">
                <label className="form-label">Atlassian URL *</label>
                <input type="url" className="form-control" placeholder="https://yourorg.atlassian.net"
                  value={globalConfig.jiraUrl}
                  onChange={e => setGlobalConfig(prev => ({ ...prev, jiraUrl: e.target.value.trim() }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Email *</label>
                <input type="email" className="form-control" placeholder="you@company.com"
                  value={globalConfig.jiraUser}
                  onChange={e => setGlobalConfig(prev => ({ ...prev, jiraUser: e.target.value.trim() }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label">API Token *</label>
                <input type="password" className="form-control"
                  placeholder={configMeta?.hasToken ? '●●●●●●●● (saved)' : 'Paste token here'}
                  value={globalConfig.apiToken} autoComplete="new-password"
                  onChange={e => setGlobalConfig(prev => ({ ...prev, apiToken: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Default JIRA Project</label>
                {jiraProjects.length > 0 ? (
                  <select className="form-select" value={globalConfig.defaultProjectKey}
                    onChange={e => setGlobalConfig(prev => ({ ...prev, defaultProjectKey: e.target.value }))}>
                    <option value="">— None —</option>
                    {jiraProjects.map(p => (
                      <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" className="form-control" placeholder="e.g. PROJ (auto-filled after connect)"
                    value={globalConfig.defaultProjectKey}
                    onChange={e => setGlobalConfig(prev => ({ ...prev, defaultProjectKey: e.target.value.toUpperCase() }))} />
                )}
              </div>
            </div>

            {/* Test result banner */}
            {testResult && (
              <div className={`alert ${testResult.connected ? 'alert-success' : 'alert-danger'} mb-3`}>
                {testResult.connected
                  ? <>✅ Connected to <strong>{testResult.jiraUrl}</strong> as <strong>{testResult.user?.displayName}</strong> ({testResult.user?.email})</>
                  : <>❌ {testResult.error}</>}
              </div>
            )}

            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-outline-secondary" onClick={testCredentials} disabled={testing}>
                {testing
                  ? <><span className="spinner-border spinner-border-sm me-2" />Testing…</>
                  : '🔍 Test Connection'}
              </button>
              <button className="btn btn-primary" onClick={saveGlobalConfig} disabled={savingGlobal}>
                {savingGlobal
                  ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                  : '💾 Save & Connect'}
              </button>
              {globalSaved && <span className="align-self-center" style={{ color: 'var(--zq-success)', fontSize: 13, fontWeight: 600 }}>✅ Saved!</span>}
              {configMeta?.hasToken && (
                <button className="btn btn-outline-danger ms-auto" onClick={disconnectJira}>
                  Disconnect
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Section 2: Per-Project JIRA project key (only when opened from a project) ── */}
        {projectId && (
          <div className="card mb-4">
            <div className="card-header">
              <span>Project JIRA Mapping{projectName ? ` — ${projectName}` : ''}</span>
            </div>
            <div className="card-body">
              <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 16 }}>
                Select which JIRA project this ZipQA project maps to. All other credentials are shared from the global connection above.
              </p>
              {projectSaved && <div className="alert alert-success py-2">✅ Project config saved.</div>}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">JIRA Project</label>
                  {jiraProjects.length > 0 ? (
                    <select className="form-select" value={projectConfigForm.projectKey}
                      onChange={e => setProjectConfigForm(prev => ({ ...prev, projectKey: e.target.value }))}>
                      <option value="">— Use global default —</option>
                      {jiraProjects.map(p => (
                        <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" placeholder="e.g. PROJ"
                      value={projectConfigForm.projectKey}
                      onChange={e => setProjectConfigForm(prev => ({ ...prev, projectKey: e.target.value.toUpperCase() }))} />
                  )}
                  <div className="form-text">Issues created from this project will default to this JIRA project.</div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={saveProjectConfig} disabled={savingProject}>
                {savingProject
                  ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                  : '💾 Save'}
              </button>
            </div>
          </div>
        )}

        {/* ── Section 3: JIRA Projects picker ── */}
        {(jiraProjects.length > 0 || loadingProjects) && (
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Accessible JIRA Projects</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={loadJiraProjects} disabled={loadingProjects}>
                {loadingProjects ? <span className="spinner-border spinner-border-sm" /> : '↺ Refresh'}
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="table table-hover mb-0">
                <thead>
                  <tr><th>Key</th><th>Name</th><th>Issue Types</th><th>Default</th></tr>
                </thead>
                <tbody>
                  {jiraProjects.map(p => {
                    const isDefault = globalConfig.defaultProjectKey === p.key;
                    return (
                      <tr key={p.key} style={isDefault ? { background: 'var(--zq-primary-light, #ede9fe)' } : {}}>
                        <td><span className="badge bg-primary">{p.key}</span></td>
                        <td style={{ fontWeight: 500 }}>{p.name}</td>
                        <td>
                          {p.issueTypes?.length > 0
                            ? p.issueTypes.slice(0, 5).map(t => (
                                <span key={t.id} className="badge me-1" style={{ background: 'var(--zq-border-light)', color: 'var(--zq-text-secondary)' }}>{t.name}</span>
                              ))
                            : <span style={{ color: 'var(--zq-text-muted)', fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ width: 140 }}>
                          {isDefault ? (
                            <span style={{ color: 'var(--zq-success, #16a34a)', fontWeight: 600, fontSize: 13 }}>
                              ✅ Default
                            </span>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-primary py-0"
                              onClick={() => setGlobalConfig(prev => ({ ...prev, defaultProjectKey: p.key }))}
                            >
                              Set as default
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {globalConfig.defaultProjectKey && (
              <div className="card-footer" style={{ fontSize: 12, color: 'var(--zq-text-muted)' }}>
                Default is only applied after you click <strong>💾 Save &amp; Connect</strong> above.
              </div>
            )}
          </div>
        )}

        {/* ── Section 4: Webhook ── */}
        {isConnected && (
          <div className="card mb-4">
            <div className="card-header">Webhook Configuration (optional)</div>
            <div className="card-body">
              <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 12 }}>
                Set up a JIRA webhook so issue updates in JIRA automatically sync back to ZipQA.
              </p>
              <ol style={{ fontSize: 13, color: 'var(--zq-text-secondary)' }}>
                <li>In JIRA: <strong>Project Settings → System → Webhooks → Create webhook</strong></li>
                <li>Set URL to: <code>{window.location.protocol}//{window.location.hostname}:4000/api/jira/webhook</code></li>
                <li>Enable events: <strong>Issue → updated</strong></li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
